import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { WorkFlowConfiguredStepAction } from '../../../../workflow.model';
import { OptionItem } from '../../../../../../../../ui-components/lib/option.interface';

export type StepActionFormGroup = FormGroup<{
  id: FormControl<number | null>;
  type: FormControl<string>;
  when: FormControl<string>;
  user_ids: FormControl<number[]>;
}>;

export interface StepActionFormValue {
  id?: number | null;
  type?: string;
  when?: string;
  user_ids?: number[];
}

export const STEP_ACTION_WHEN_OPTIONS = [
  {
    label: 'workflow_step_info_form_step_triggered_label',
    value: 'triggered',
  },
  {
    label: 'workflow_step_info_form_step_retriggered_label',
    value: 're_triggered',
  },
  {
    label: 'workflow_step_info_form_step_approved_or_completed_label',
    value: 'approved_or_completed',
  },
  {
    label: 'workflow_step_info_form_step_rejected_label',
    value: 'rejected',
  },
  {
    label: 'workflow_step_info_form_step_sent_back_label',
    value: 'sent_back',
  },
];

export const STEP_ACTION_TYPE_OPTIONS = [
  {
    label: 'workflow_step_info_form_add_collaborators_action_label',
    value: 'add_collaborators',
  },
];

export const DUPLICATED_ACTION_ERROR = 'duplicatedAction';

export interface DuplicatedActionError {
  duplicatedWithIndex: number;
}

export const requiredUserIdsValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const value = control.value;
  return Array.isArray(value) && value.length > 0 ? null : { required: true };
};

function getStepActionCombinationKey(group: AbstractControl): string | null {
  const when = group.get('when')?.value;
  const type = group.get('type')?.value;

  if (!when || !type) {
    return null;
  }

  return `${when}::${type}`;
}

export const duplicatedStepActionValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const parent = control.parent;

  if (!(parent instanceof FormArray)) {
    return null;
  }

  const combinationKey = getStepActionCombinationKey(control);

  if (!combinationKey) {
    return null;
  }

  const currentIndex = parent.controls.indexOf(control);
  const duplicatedWithIndex = parent.controls.findIndex(
    (sibling, index) =>
      index < currentIndex &&
      getStepActionCombinationKey(sibling) === combinationKey
  );

  return duplicatedWithIndex >= 0
    ? {
        [DUPLICATED_ACTION_ERROR]: {
          duplicatedWithIndex: duplicatedWithIndex + 1,
        } satisfies DuplicatedActionError,
      }
    : null;
};

export const uniqueStepActionCombinationValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  if (!(control instanceof FormArray)) {
    return null;
  }

  for (const group of control.controls) {
    group.updateValueAndValidity({ emitEvent: false, onlySelf: true });
  }

  const hasDuplicate = control.controls.some(group =>
    group.hasError(DUPLICATED_ACTION_ERROR)
  );

  return hasDuplicate ? { [DUPLICATED_ACTION_ERROR]: true } : null;
};

export function createStepActionFormGroup(
  action: StepActionFormValue = {}
): StepActionFormGroup {
  return new FormGroup(
    {
      id: new FormControl<number | null>(action.id ?? null),
      type: new FormControl(action.type ?? '', {
        nonNullable: true,
        validators: Validators.required,
      }),
      when: new FormControl(action.when ?? '', {
        nonNullable: true,
        validators: Validators.required,
      }),
      user_ids: new FormControl<number[]>(action.user_ids ?? [], {
        nonNullable: true,
        validators: requiredUserIdsValidator,
      }),
    },
    { validators: duplicatedStepActionValidator }
  );
}

export function createStepActionsFormArray(
  groups: StepActionFormGroup[] = [createStepActionFormGroup()]
): FormArray<StepActionFormGroup> {
  return new FormArray(groups, {
    validators: uniqueStepActionCombinationValidator,
  });
}

export function createStepActionFormGroupFromConfig(
  action: WorkFlowConfiguredStepAction
): StepActionFormGroup {
  return createStepActionFormGroup({
    id: action.id,
    type: action.type?.value != null ? String(action.type.value) : '',
    when: action.when?.value != null ? String(action.when.value) : '',
    user_ids: (action.users ?? []).map(user => user.id),
  });
}

export function mapStepActionUsersToOptions(
  users: WorkFlowConfiguredStepAction['users']
): OptionItem[] {
  return (users ?? []).map(user => ({
    id: user.id,
    name: user.name,
    label: user.name,
    value: user.id,
    image: user.image ?? undefined,
  }));
}
